/**
 * listingLikes.ts — the heart likes for real.
 *
 * Blake shipped the hook in #723: `<button class="social-like"
 * data-likelisting="${l.id}">` on both the feed card and the listing detail,
 * with NO click handler. That is the right shape and worth saying — there is no
 * fabricator to suppress here and no `GUARDED_WRITES` entry needed. The heart
 * was honestly inert; this makes it work.
 *
 * ── THREE ENDPOINTS, AND THEY GIVE US MORE THAN THE VOTE HAS ────────────────
 *
 *   GET    /api/users/me/likes            -> { likedIds: string[] }
 *   POST   /api/listings/:id/like         -> { ok, liked, likes }   (listings.ts)
 *   DELETE /api/users/me/likes/:listingId -> { ok, liked, likes }   (users.ts)
 *
 * The first is why this can do something `listingVotes` cannot: paint the
 * viewer's OWN state on a cold load. One request answers the whole viewer,
 * rather than follow's one-status-call-per-button.
 *
 * 🔑 THE SERVER'S ANSWER IS BELIEVED, ON BOTH ROUTES (2026-09-20). Both now send
 * `liked` and `likes`, the state AFTER the write. That matters most when the
 * write half-fails: if the count update throws, the server undoes the like row
 * (or puts an unliked one back) and still answers 200, with `liked` saying what
 * is true. A like answered `liked: false` used to leave an empty heart and no
 * message, which on a phone read as a red outline that never filled (the stuck
 * :hover) and nothing else (Blake, 2026-09-18). An unlike ignored the body
 * altogether, so an unlike the server could not make painted a real like away.
 * Now the heart and the number are whatever the server said, and a write that
 * did not take says so. The arithmetic below is only the fallback for a body
 * that carries no `liked` or `likes`.
 *
 * 🪤 HOW THE FALLBACK GOT HERE, kept because the lesson is still good. The
 * server once answered `{ ok }` and nothing else.
 * The first draft believed POST returned the authoritative total and gated the
 * whole update on it, so for two weeks a like wrote its row and bumped the
 * column while the heart and the number on the card never moved (Blake,
 * 2026-09-06: "it highlights the heart but the number doesn't move"; the
 * highlight was :hover, the same red as .on). The like now derives the count
 * the way the unlike always has and the way the app paints it: the number the
 * card shows plus one, which is what the column the card was painted from now
 * holds (listings.ts increments it, and the liked map stops a repeat before it
 * gets here). DELETE derives `GREATEST(likes - 1, 0)`, the server's own rule.
 * If the server ever sends `likes`, that total wins over both. If the current
 * count cannot be read, the heart still turns on and the number is left alone
 * rather than replaced with a made-up one.
 *
 * 🔴 THIS ONE STOPS PROPAGATION, unlike the rest of the lane. When this was
 * written his card-open handler excluded eight selectors and
 * `[data-likelisting]` was not among them (it is now, the first exclusion in
 * app.js's `.lcard[data-listing]` click handler, so this is belt and braces
 * today). Without either, a tap on the heart falls through and NAVIGATES to the
 * listing detail, the exact thing his own ruling forbids ("likes in place, never
 * navigates"). There is no guard entry to preserve here and no handler of his to
 * let run, so this is the `accountActions` posture: the control is deliberately
 * unguarded and we own the suppression. Raised on #723; if the exclusion lands
 * in his file too, this stays correct and simply stops being load-bearing.
 *
 * 🪤 THE PARAGRAPH THAT USED TO SIT HERE WAS WRONG, AND HOW IT WAS WRONG IS WORTH
 * KEEPING. It said `.social-like` has no `.on` rule at all, so a liked heart
 * could not be shown as liked. The grep behind that claim ran against a checkout
 * cut EIGHT MINUTES before #723 merged the rule (styles.css 542-543, plus five
 * hits in styles.scoped.css). The instrument was fine; the tree was older than
 * the fix. A zero from a grep is a fact about the tree you grepped, and nothing
 * more.
 *
 * 🔑 SO THE STATE IS PAINTED HERE — AND RE-PAINTED AFTER EVERY REPAINT.
 * His bundle replaces #app wholesale on every render, so a class set once
 * survives until the next hashchange or handler and then the heart silently
 * reads unliked over a like that really happened. Same conclusion followUser
 * reached about its label: it has to be re-asserted, not asserted.
 *
 * 🔴 OUR OWN WRITE REPAINTS NOTHING (2026-09-20). A like used to answer itself
 * with rerenderKeepScroll(), which keeps the page's vertical scroll and nothing
 * else: render() replaces #app, so every .rail is a new node and a rail the
 * visitor had scrolled sideways jumped back to its start (Blake, 2026-09-18).
 * The liked list arriving on a cold load did the same with no tap at all. Now
 * the number is written into the heart that is already on screen, the row is
 * updated for his next paint, and the class is applied. The profile's "Buzzing
 * Right Now" tile words the same count beside hearts for the same listing, so
 * it carries `data-likeslabel` and is patched with them. The Insights pop-up
 * paints the count too, but it covers the page while it is open, and opening it
 * is a paint.
 *
 * 🔴 PAINTED FROM THE TAP, SETTLED BY THE ANSWER (card 1219177901097382,
 * 2026-10-20). The heart and the number move the moment they are tapped: the
 * row through the bridge, the nodes in place, the count by the server's own
 * arithmetic. The answer then settles them (its `liked` and `likes` win, as
 * before), and anything that is not a 2xx puts them back where they were and
 * says why in the server's words. A request that never answers within
 * instantTap.ts's budget is put back too, and the liked list is re-read rather
 * than the like resent; a late answer still settles the press when no newer
 * tap came. A second tap while one is out is still dropped (decision 3).
 * Nothing here reads the rate limiter's number or its bucket (decision 1).
 *
 * ⚠️ Unlike Follow, a like is TWO-WAY. followUser only ever adds its state, so
 * it can skip anything already marked. An unlike has to REMOVE the class, so the
 * idempotence check runs in both directions — otherwise the observer's own
 * mutation re-enters applyLikeState forever.
 *
 * The colour is HIS: `.social-like.on { color: var(--destructive) }`, red,
 * deliberately mirroring `take-like.on` so a liked heart reads the same
 * everywhere. Gold stays CTA-reserved. We apply his class and set no styling.
 */

import { signedInOrWall } from "./guestWall";
import { awaitTap, createClock, isNoAnswer, readIsWayBack, settleOutranksRead, type Read } from "./instantTap";

export const LIKE_SELECTOR = "[data-likelisting]";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't update that like — please try again";
export const SIGNIN_MESSAGE = "Sign in to like a listing";
/** After the budget ran out and the re-read could not say either way. */
export const UNCONFIRMED_MESSAGE = "Couldn't confirm that like. Check the heart before tapping again.";

interface LikeWindow extends Window {
  toast?: (message: string) => void;
  __gbzSetListingLike?: (id: string, likes: number | null, liked: boolean) => boolean;
  __gbzListingLiked?: (id: string) => boolean;
  __gbzListingLikes?: (id: string) => number | null;
  __gbzSeedLikedMap?: (ids: string[]) => number;
}

export function installListingLikes(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as LikeWindow).toast?.(message);
    },
    /* Whether anyone is signed in, as WebInstall's boot knows it (identity is fixed for the tab until a
       reload). A guest's /api/users/me/likes only ever answered 401, so a guest's install skips the seed read
       (lane D's speed audit, win 4). Left out, the install reads, as it always did. */
    signedIn = true,
  }: { notify?: (m: string) => void; signedIn?: boolean } = {},
): () => void {
  /* In flight, per listing. His control had no re-entrancy guard because it had
     no handler at all; with a real await a double-tap is a like racing its own
     unlike, and the second lands on a baseline the first already moved. */
  const busy = new Set<string>();

  /**
   * Write a listing's new total into every heart for it that is on screen, then
   * apply the class. Never a repaint: see the header.
   *
   * His two hearts both end in a bare text node that holds the number, "7" on
   * the card and " 7 likes" on the listing page (app.js renderCard and
   * renderDetail). On the card only the digits are replaced. On the listing page
   * the word follows the number, "1 like" and "2 likes", the rule app.js's
   * likesLabel paints with (Blake 2026-09-21). A heart with no number in its last
   * text node is left alone. `total` is null when no honest number is known; the
   * class still moves.
   *
   * A text edit is a characterData mutation, which the childList observer below
   * does not see, so this cannot feed it.
   */
  function paintInPlace(listingId: string, total: number | null): void {
    if (total !== null) {
      for (const el of Array.from(win.document.querySelectorAll<HTMLElement>(LIKE_SELECTOR))) {
        if (el.dataset.likelisting !== listingId) continue;
        const tail = el.lastChild;
        if (!tail || tail.nodeType !== 3 /* TEXT_NODE */) continue;
        const text = tail.textContent ?? "";
        if (!/\d/.test(text)) continue;
        const word = total === 1 ? "like" : "likes";
        const next = /\d[\d,]*\s+likes?\b/.test(text)
          ? text.replace(/\d[\d,]*(\s+)likes?\b/, (_m, gap: string) => String(total) + gap + word)
          : text.replace(/\d[\d,]*/, String(total));
        if (next !== text) tail.textContent = next;
      }
      /* Anything else on screen that words this listing's count (app.js marks it). */
      const label = `${total} ${total === 1 ? "like" : "likes"}`;
      for (const el of Array.from(win.document.querySelectorAll<HTMLElement>("[data-likeslabel]"))) {
        if (el.dataset.likeslabel === listingId && el.textContent !== label) el.textContent = label;
      }
    }
    /* Directly, not only through the observer: the observer is for HIS repaints
       (hashchange, his handlers), and nothing of ours mutates the tree now. */
    applyLikeState();
  }

  interface LikeState { liked: boolean; likes: number | null }

  /* ── THE ORDER OF EVENTS (instantTap.ts header). One clock for this module; the
     seed, every press and every settle take their place on it. ── */
  const tick = createClock();
  /** The liked list as last read: what it said, when it was sent, when it landed. */
  let seed: Read<Set<string>> | undefined;
  /** Presses out right now, by listing: the liked state each one painted. Set at the
      paint, cleared at the settle or the put-back, so a seed landing later never
      re-asserts a guess the screen has already taken back (pass 2, finding 1). */
  const pending = new Map<string, boolean>();
  /** The last settled state per listing (an answer or a re-read), and when. */
  const settled = new Map<string, { liked: boolean; likes: number | null; at: number }>();
  /** The latest press per listing. A late answer for an older press changes nothing. */
  const presses = new Map<string, number>();

  /**
   * Seed the viewer's liked set once, so a cold load paints what they already
   * liked.
   *
   * Best-effort, like every other lazy read in this lane: a failure leaves the
   * hearts reading as unliked, which is wrong in the same direction they were
   * already wrong before this existed — never a false positive, never a like
   * claimed that is not there.
   *
   * The seed replaces the map wholesale (floorTransport.ts __gbzSeedLikedMap), so
   * it is followed by two re-asserts: every press still painted, and every
   * settle newer than the seed's own send (a seed sent at install and landing
   * after a tap's answer is older than that answer).
   */
  async function seedLikes(): Promise<void> {
    if (!signedIn) return;
    const sentAt = tick();
    const res = await authFetch("/api/users/me/likes");
    if (!res.ok) return;
    const body = (await res.json().catch(() => ({}))) as { likedIds?: string[] };
    if (!Array.isArray(body.likedIds)) return;
    const w = win as LikeWindow;
    w.__gbzSeedLikedMap?.(body.likedIds);
    seed = { value: new Set(body.likedIds), sentAt, landedAt: tick() };
    /* Settles newer than the seed's send first, presses in flight LAST: a press's
       own intent outranks any truth while its answer is out (pass 3, finding 1). */
    for (const [id, s] of settled) if (settleOutranksRead(s.at, sentAt)) w.__gbzSetListingLike?.(id, null, s.liked);
    for (const [id, want] of pending) w.__gbzSetListingLike?.(id, null, want);
    /* A seed carries no counts, only which hearts are on. The caller applies the
       class; a repaint here moved every rail on the page for nothing. */
  }

  function read(listingId: string): LikeState {
    const w = win as LikeWindow;
    return { liked: w.__gbzListingLiked?.(listingId) ?? false, likes: w.__gbzListingLikes?.(listingId) ?? null };
  }

  /** The row and every heart on screen, from one state: the tap's guess, the server's answer, or the way back. */
  function show(listingId: string, state: LikeState): void {
    (win as LikeWindow).__gbzSetListingLike?.(listingId, state.likes, state.liked);
    paintInPlace(listingId, state.likes);
  }

  /**
   * The count after `liked` changes, the server's own arithmetic: plus one on a
   * like, GREATEST(n-1, 0) on an unlike (users.ts), unchanged when nothing
   * moved, and null when no honest number is known (see the header: a number
   * we invented would be a worse answer than a stale one).
   */
  function derive(from: LikeState, liked: boolean): number | null {
    if (from.likes === null) return null;
    if (liked === from.liked) return from.likes;
    return liked ? from.likes + 1 : Math.max(0, from.likes - 1);
  }

  /** The press, settled: the state is the server's; the guess is no longer on screen. */
  function settle(listingId: string, state: LikeState): void {
    settled.set(listingId, { liked: state.liked, likes: state.likes, at: tick() });
    pending.delete(listingId);
    show(listingId, state);
  }

  /** The press, put back: the guess is no longer on screen. */
  function putBack(listingId: string, back: () => LikeState): void {
    pending.delete(listingId);
    show(listingId, back());
  }

  /**
   * The server's answer, applied. `liked` and `likes` win (see the header); a
   * body without them falls back to the arithmetic from the way back, never
   * from the guess already on screen. A 200 with the opposite of what was asked
   * (the server took the like back, or could not remove it) is painted as the
   * server says and said out loud. `still`, for a LATE answer: whether this press
   * is still the latest, asked again after the body is read, because a newer
   * press can start while the body is in flight (pass 2, finding 4).
   */
  async function apply(listingId: string, want: boolean, res: Response, back: () => LikeState, still: () => boolean = () => true): Promise<void> {
    if (res.status === 401) {
      putBack(listingId, back);
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* Put back, then the server's own words. 409 is "Listing is not available
         to like" — the row left a likeable status — 503 is "Activity storage
         unavailable", a real outage, and 429 is the limiter's sentence: one more
         refusal, handled like the rest (decision 1, 2026-10-20: no code here
         reads the limiter's number or its bucket). */
      const b = back();
      putBack(listingId, back);
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      if (!still()) return;
      /* No words when the way back already shows what was asked (this press's own
         re-read settled it, or the seed said so): "try again" would invite the
         opposite tap (pass 4, finding 2). */
      if (b.liked === want) return;
      notify(err.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }
    const body = (await res.json().catch(() => ({}))) as { liked?: boolean; likes?: number };
    if (!still()) return;
    const liked = typeof body.liked === "boolean" ? body.liked : want;
    const likes = typeof body.likes === "number" ? body.likes : derive(back(), liked);
    settle(listingId, { liked, likes });
    if (liked !== want) notify(GENERIC_FAILURE_MESSAGE);
  }

  /**
   * After the budget ran out: the liked list, re-read, says whether the write
   * landed. `stale` is true once the original request answered after all: its
   * answer is the truth about its own write, so the re-read then changes
   * nothing. Checked AFTER the body is read, because a late answer can land
   * between this read's headers and its body.
   */
  async function reread(listingId: string, want: boolean, back: () => LikeState, stale: () => boolean): Promise<void> {
    const outcome = await awaitTap(authFetch("/api/users/me/likes"));
    const body = outcome.kind === "answer" && outcome.res.ok
      ? ((await outcome.res.json().catch(() => ({}))) as { likedIds?: unknown })
      : {};
    if (stale()) return;
    if (!Array.isArray(body.likedIds)) {
      notify(UNCONFIRMED_MESSAGE);
      return;
    }
    const liked = body.likedIds.includes(listingId);
    settle(listingId, { liked, likes: derive(back(), liked) });
    if (liked !== want) notify(GENERIC_FAILURE_MESSAGE);
  }

  async function toggle(listingId: string): Promise<void> {
    const before = read(listingId);
    const want = !before.liked;
    /* 🔴 PAINTED FROM THE TAP (card 1219177901097382). The heart and the number
       move now, on the nodes already on screen; the server's answer settles
       them; a refusal, a dropped request or a silent one puts them back. */
    show(listingId, { liked: want, likes: derive(before, want) });
    const paintedAt = tick();
    pending.set(listingId, want);
    const press = (presses.get(listingId) ?? 0) + 1;
    presses.set(listingId, press);
    /* Busy holds the control for the whole press, so only a LATE answer (after the
       press is over) has to ask whether a newer press has taken the control. */
    const current = (): boolean => presses.get(listingId) === press;
    /* The way back: the seed's answer for this heart when the seed is the way back
       (it landed since the paint and was sent after this heart's last settle), else
       the tap-time state. The count is the tap-time one either way: a seed carries
       no counts. A heart liked last week, tapped on a cold load before the seed
       lands, then refused, goes back to liked; a seed sent at boot and landing
       after a later tap's answer is older than that answer and is ignored. */
    const back = (): LikeState => {
      const s = settled.get(listingId);
      /* This press's own re-read settled while a late answer was still out: it is the
         newest truth, and a late refusal goes back to it (pass 3, finding 2). */
      if (s && s.at > paintedAt) return { liked: s.liked, likes: s.likes };
      if (readIsWayBack(seed, paintedAt, s?.at)) return { liked: seed!.value.has(listingId), likes: before.likes };
      return before;
    };

    const request = want
      ? authFetch(`/api/listings/${encodeURIComponent(listingId)}/like`, { method: "POST" })
      : authFetch(`/api/users/me/likes/${encodeURIComponent(listingId)}`, { method: "DELETE" });
    const outcome = await awaitTap(request);
    if (outcome.kind === "answer") {
      await apply(listingId, want, outcome.res, back);
      return;
    }
    const b = back();
    putBack(listingId, back);
    if (outcome.kind === "thrown") {
      /* Offline, DNS, a refused socket: nothing reached the server (a resend of a
         like is idempotent, so no read). Silent when the way back already shows
         what was asked. */
      if (b.liked !== want) notify(GENERIC_FAILURE_MESSAGE);
      return;
    }
    /* Expired: the server MAY have taken it. Re-read rather than resend, and let
       a late answer still settle this press (the write landed after all, so the
       screen catches up to it) unless a newer tap has taken the heart since. */
    let answered = false;
    void request.then(
      (res) => {
        /* A late 502/504 says nothing about the write: the re-read decides (pass 5). */
        if (isNoAnswer(res.status)) return;
        answered = true;
        if (current()) void apply(listingId, want, res, back, current);
      },
      () => { /* a request that failed after the budget: the re-read is the truth */ },
    );
    await reread(listingId, want, back, () => answered);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(LIKE_SELECTOR) as HTMLElement | null;
    if (!el) return;
    const listingId = el.dataset.likelisting;
    if (!listingId) return;

    /* See the header. His card-open handler does not exclude this control, so
       without stopping here a tap on the heart navigates to the detail page.
       stopPropagation, never stopImmediatePropagation — we own other document
       capture listeners and killing them would be a far worse trade. */
    event.stopPropagation();
    event.preventDefault();

    // A guest gets the sign-in wall and no request — see guestWall.ts.
    if (!signedInOrWall(win)) return;
    if (busy.has(listingId)) return;
    busy.add(listingId);
    void toggle(listingId)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(listingId));
  };

  /**
   * Paint the viewer's own liked state onto every heart on screen.
   *
   * Reads the bridge's liked map rather than any DOM state, so the class is a
   * VIEW of what we know and never a source of truth that could drift from it.
   *
   * 🪤 IDEMPOTENT IN BOTH DIRECTIONS, and that is load-bearing. The observer
   * below fires on our own mutations too; if this wrote unconditionally it
   * would re-enter itself forever. `contains` is checked before every add AND
   * before every remove, so a heart already in the right state is untouched and
   * the observer sees nothing to react to.
   *
   * An `undefined` answer is LEFT ALONE rather than forced off. Today's bridge
   * never gives one (`__gbzListingLiked` answers true or false, and an unseeded
   * map reads false), so this guards a bridge that is older or not there yet:
   * painting a real like away is the one failure this feature cannot afford.
   */
  function applyLikeState(): void {
    const w = win as LikeWindow;
    if (typeof w.__gbzListingLiked !== "function") return;
    for (const el of Array.from(win.document.querySelectorAll<HTMLElement>(LIKE_SELECTOR))) {
      const id = el.dataset.likelisting;
      if (!id) continue;
      const liked = w.__gbzListingLiked(id);
      if (liked === undefined) continue;
      if (liked) {
        if (!el.classList.contains("on")) el.classList.add("on");
      } else if (el.classList.contains("on")) {
        el.classList.remove("on");
      }
    }
  }

  win.document.addEventListener("click", onClick, true);

  /* Same posture as followUser and demoTextFixups: his bundle repaints
     constantly, so the state has to be re-applied rather than applied. Scoped
     to #app, the node his render actually replaces. */
  const observer = new MutationObserver(applyLikeState);
  const root = win.document.getElementById("app") ?? win.document.body;
  if (root) observer.observe(root, { childList: true, subtree: true });

  void seedLikes()
    .then(applyLikeState)
    .catch(() => {
      /* A dropped seed is not an answer. The hearts read unliked, which is where
         they were before this module existed. */
    });
  /* Paint whatever the map already knows at install time, before any repaint or
     seed lands — a listing detail entered directly should not flash unliked. */
  applyLikeState();

  return () => {
    observer.disconnect();
    win.document.removeEventListener("click", onClick, true);
  };
}
